import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Sparkles, Play, CheckCircle2, Clock, Loader2, CalendarClock, ShieldCheck, MessageCircleHeart, Star, Lock, Radio } from 'lucide-react';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { AppointmentTracker } from './AppointmentTracker';
import { VideoPlayerDialog } from './VideoPlayerDialog';
import { CoachPaywallDialog } from './CoachPaywallDialog';
import { TodayExtraCard } from './TodayExtraCard';
import { MissedClassCard } from './MissedClassCard';

const RATING_LABELS = { 1: 'Too intense', 2: 'A bit much', 3: 'Just right', 4: 'Comfortable', 5: 'Could do more' };

const MODE_BADGES = {
  hard_block: { label: 'Doctor-first safe mode', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
  high_risk: { label: 'Extra-care mode', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  conservative: { label: 'Gentle week', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
};

export const WeekPlan = ({ status, onStatusRefresh }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playVideo, setPlayVideo] = useState(null);
  const [rateVideo, setRateVideo] = useState(null);
  const [rating, setRating] = useState(0);
  const [saving, setSaving] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [planVersion, setPlanVersion] = useState(0);

  const conditions = status?.config?.conditions || [];
  const currentConditions = status?.onboarding?.conditions || [];
  const watchPct = status?.config?.watch_complete_pct ?? 90;

  const onProgress = useCallback((videoId, data) => {
    setPlan((p) => p ? { ...p, videos: (p.videos || []).map((v) => (v.video_id === videoId && v.status === 'pending') ? { ...v, watch_pct: data.watch_pct } : v) } : p);
  }, []);

  const openIntro = async () => {
    try {
      const r = await api.get('/api/prenatal-coach/intro');
      setPlayVideo({ intro: true, title: r.data.video?.title || 'Meet your SoulAI Coach', playback: r.data.playback ? { ready: true, ...r.data.playback } : { ready: false, message: 'The intro video is still being prepared.' } });
    } catch { toast.error('Could not load the intro video'); }
  };

  const fetchPlan = useCallback(() => {
    setLoading(true);
    api.get('/api/prenatal-coach/plan')
      .then((r) => { setPlan(r.data); setPlanVersion((n) => n + 1); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not load your plan'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const refreshAll = () => { fetchPlan(); onStatusRefresh?.(); };

  const submitRating = async () => {
    setSaving(true);
    try {
      const r = await api.post('/api/prenatal-coach/videos/complete', { video_id: rateVideo.video_id, rating });
      toast.success('Lovely! Noted for next week\'s plan.');
      setRateVideo(null); setRating(0); refreshAll();
      if (r.data?.paywall) setTimeout(() => setPaywallOpen(true), 600);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="coach-plan-loading">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }
  if (!plan) return null;

  // ── Waiting-for-scan state (polite hold) ──
  if (plan.waiting_for_scan) {
    return (
      <div className="space-y-4 max-w-2xl" data-testid="coach-waiting-for-scan">
        <Card className="rounded-2xl border-primary/20 bg-gradient-to-br from-primary/5 to-rose-50">
          <CardContent className="pt-6 pb-6 text-center space-y-3">
            <ShieldCheck className="h-10 w-10 text-primary mx-auto" />
            <h2 className="font-heading text-lg font-semibold">Your plan starts right after your first scan</h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">{plan.message}</p>
          </CardContent>
        </Card>
        <AppointmentTracker appointments={plan.appointments || []} conditions={conditions}
          currentConditions={currentConditions} onChanged={refreshAll} />
      </div>
    );
  }

  const videos = plan.videos || [];
  const completedCount = videos.filter((v) => v.status === 'completed').length;
  const modeBadge = MODE_BADGES[plan.mode];
  const access = plan.access || status?.access || {};
  const isFree = access.level === 'free';
  const freeTier = access.free_tier || {};
  const freeWatched = freeTier.cumulative_pct || 0;
  const lockedCount = videos.filter((v) => v.locked).length;

  return (
    <div className="space-y-4" data-testid="coach-week-plan">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">This Week's Practice</h1>
            {modeBadge && <Badge className={`${modeBadge.cls} text-[10px]`} data-testid="plan-mode-badge">{modeBadge.label}</Badge>}
            {access.level === 'full' && access.plan && (
              <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]" data-testid="plan-access-badge">
                {access.plan === 'till_delivery' ? 'Till Delivery' : access.plan === 'monthly' ? 'Monthly plan' : access.plan === 'elite_addon' ? 'Elite' : 'Active'}
                {access.days_left != null && access.plan !== 'till_delivery' ? ` · ${access.days_left}d left` : ''}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5" data-testid="plan-subtitle">
            {plan.stage === 'postnatal'
              ? `Week ${plan.weeks_postpartum ?? '–'} postpartum • Recovery plan`
              : `Week ${plan.pregnancy_week} • Trimester ${plan.trimester}`} • {completedCount} of {videos.length} done
            {plan.daily_gate?.one_per_day ? ' • one class a day' : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" data-testid="speak-to-expert-btn"
          onClick={() => window.dispatchEvent(new CustomEvent('open-soulai', { detail: { expertMode: true } }))}>
          <MessageCircleHeart className="h-4 w-4 mr-1.5" />Speak to Expert
        </Button>
      </div>

      {/* Free tier banner — V72: a cumulative 90% watch budget, not one pinned class */}
      {isFree && (
        <Card className={`rounded-2xl ${access.paywall ? 'border-primary/40 bg-primary/5' : 'border-amber-200 bg-amber-50/70'}`} data-testid="coach-free-tier-banner">
          <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
            <div className="h-9 w-9 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0"><Lock className="h-4 w-4 text-primary" /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium">
                {access.paywall ? 'Your free class is complete — beautifully done.' : 'Your first class is free.'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {access.paywall
                  ? `Unlock the remaining ${lockedCount} classes this week and a fresh, safety-matched plan every week.`
                  : freeWatched > 0
                    ? `You're ${freeWatched}% through it — it stays open until you've finished, however many weeks that takes.`
                    : `Finish it to unlock the rest of your weekly plan (${lockedCount} more classes waiting).`}
              </p>
              {!access.paywall && freeWatched > 0 && (
                <div className="mt-2 h-1.5 w-full max-w-[220px] rounded-full bg-amber-200/70 overflow-hidden"
                  data-testid="coach-free-budget-bar">
                  <div className="h-full rounded-full bg-primary transition-[width] duration-500"
                    style={{ width: `${Math.min(100, Math.round((freeWatched / (freeTier.budget_pct || 90)) * 100))}%` }} />
                </div>
              )}
            </div>
            <Button size="sm" onClick={() => setPaywallOpen(true)} data-testid="coach-unlock-btn">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />{access.paywall ? 'Unlock full plan' : 'See plans'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* AI narrative */}
      {plan.ai_narrative && (
        <Card className="rounded-2xl border-primary/20 bg-gradient-to-br from-primary/5 to-rose-50/60" data-testid="coach-narrative-card">
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4 text-primary" /></div>
            <p className="text-sm leading-relaxed text-foreground/85">{plan.ai_narrative}</p>
          </CardContent>
        </Card>
      )}

      {/* Intro video (once) */}
      {plan.intro?.available && !plan.intro?.watched && (
        <Card className="rounded-2xl border-primary/30 bg-white" data-testid="coach-intro-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-3 flex-wrap">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Play className="h-4 w-4 text-primary" /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium">Start here: meet your SoulAI Coach</p>
              <p className="text-xs text-muted-foreground mt-0.5">{plan.intro.video?.title}{plan.intro.video?.duration_minutes ? ` · ${plan.intro.video.duration_minutes} min` : ''} — how your weekly plan works and how to stay safe.</p>
            </div>
            <Button size="sm" variant="outline" onClick={openIntro} data-testid="coach-intro-play-btn"><Play className="h-3.5 w-3.5 mr-1.5" />Watch intro</Button>
          </CardContent>
        </Card>
      )}

      {/* Live-class sync notes */}
      {(plan.live_sync?.notes || []).length > 0 && (
        <Card className="rounded-2xl border-sky-200 bg-sky-50/70" data-testid="coach-live-sync-card">
          <CardContent className="pt-3.5 pb-3.5 flex items-start gap-3">
            <Radio className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" />
            <div className="text-xs text-sky-900 leading-relaxed space-y-1">
              {plan.live_sync.notes.slice(0, 3).map((n, i) => <p key={i}>{n}</p>)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nudges */}
      {(plan.nudges || []).map((n) => (
        <Card key={n.key || n.type} className="rounded-2xl border-amber-200 bg-amber-50" data-testid={`coach-nudge-${n.type}`}>
          <CardContent className="pt-3.5 pb-3.5 flex items-center gap-3">
            <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-900 leading-relaxed">{n.message}</p>
          </CardContent>
        </Card>
      ))}

      {/* Video list */}
      {videos.length === 0 ? (
        <Card className="rounded-2xl"><CardContent className="py-10 text-center">
          <p className="text-sm text-muted-foreground">Your classes are being prepared — check back very soon!</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2.5">
          {videos.map((v) => (
            <Card key={v.video_id} className={`rounded-2xl transition-colors ${v.status === 'completed' ? 'bg-emerald-50/50 border-emerald-100' : v.locked ? 'opacity-75 bg-muted/30' : ''}`}
              data-testid={`plan-video-${v.video_id}`}>
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex flex-col items-center justify-center shrink-0 ${v.locked ? 'bg-muted' : 'bg-primary/10'}`}>
                  {v.locked ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : <span className="text-[10px] font-semibold text-primary uppercase">{v.day_label}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{v.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <Badge className={`text-[10px] ${v.intensity === 'moderate' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                      {v.intensity}
                    </Badge>
                    {v.duration_minutes && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{v.duration_minutes} min</span>}
                    {v.focus_area && <span className="text-xs text-muted-foreground capitalize">{String(v.focus_area).replace(/_/g, ' ')}</span>}
                    {v.optional && <Badge variant="outline" className="text-[10px]">optional today</Badge>}
                    {v.swapped_from && <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-800">adjusted for live class</Badge>}
                  </div>
                  {v.live_note && <p className="text-[11px] text-sky-800 mt-1" data-testid={`video-live-note-${v.video_id}`}>{v.live_note}</p>}
                  {v.status === 'pending' && !v.locked && (v.watch_pct || 0) > 0 && (
                    <div className="flex items-center gap-2 mt-1.5" data-testid={`video-watch-${v.video_id}`}>
                      <Progress value={v.watch_pct} className="h-1 w-28" />
                      <span className="text-[10px] text-muted-foreground">{v.watch_pct}% watched</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {v.status === 'completed' ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]" data-testid={`video-completed-${v.video_id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />Done{v.rating ? ` • ${RATING_LABELS[v.rating]}` : ''}
                    </Badge>
                  ) : v.status === 'skipped_live' ? (
                    <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px]" data-testid={`video-skipped-live-${v.video_id}`}>
                      <Radio className="h-3 w-3 mr-1" />Live class ✓
                    </Badge>
                  ) : v.locked && v.lock_reason === 'UPGRADE_REQUIRED' ? (
                    <Button size="sm" variant="outline" className="h-8 text-muted-foreground" onClick={() => setPaywallOpen(true)} data-testid={`locked-video-${v.video_id}`}>
                      <Lock className="h-3.5 w-3.5 mr-1" />Unlock
                    </Button>
                  ) : v.locked ? (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid={`daylocked-video-${v.video_id}`}>
                      <Clock className="h-3 w-3 mr-1" />{v.lock_reason === 'ONE_PER_DAY' ? 'Tomorrow' : `Unlocks ${v.available_on}`}
                    </Badge>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setPlayVideo(v)} data-testid={`play-video-${v.video_id}`}>
                        <Play className="h-3.5 w-3.5 mr-1" />Play
                      </Button>
                      <Button size="sm" className="h-8" disabled={(v.watch_pct || 0) < watchPct}
                        title={(v.watch_pct || 0) < watchPct ? `Rating opens at ${watchPct}% watched` : 'Rate & complete'}
                        onClick={() => { setRateVideo(v); setRating(0); }} data-testid={`complete-video-${v.video_id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Done
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* V72 — SoulAI's follow-up to a live class she booked and didn't join */}
      {plan.stage !== 'postnatal' && (
        <MissedClassCard refreshKey={planVersion} watchPct={watchPct} onPlay={setPlayVideo}
          onUnlock={() => setPaywallOpen(true)} />
      )}

      {/* V31 — daily intensity cap: one more short class (after today's plan class) */}
      {plan.stage !== 'postnatal' && (
        <TodayExtraCard refreshKey={planVersion} watchPct={watchPct} onPlay={setPlayVideo}
          onRate={(v) => { setRateVideo(v); setRating(0); }} onUnlock={() => setPaywallOpen(true)} />
      )}

      {/* Appointment tracker */}
      <AppointmentTracker appointments={plan.appointments || []} conditions={conditions}
        currentConditions={currentConditions} onChanged={refreshAll} />

      <VideoPlayerDialog video={playVideo} open={!!playVideo} onProgress={onProgress}
        onClose={() => { const wasIntro = playVideo?.intro; setPlayVideo(null); if (wasIntro) { api.post('/api/prenatal-coach/intro/watched').catch(() => {}); } fetchPlan(); }} />
      <CoachPaywallDialog open={paywallOpen} onClose={() => setPaywallOpen(false)} onPurchased={refreshAll}
        reason={access.paywall ? undefined : 'Your first class is on us. Unlock the full weekly plan whenever you are ready — every class is matched to your pregnancy week and health profile.'} />

      {/* Rating dialog */}
      <Dialog open={!!rateVideo} onOpenChange={(v) => !v && setRateVideo(null)}>
        <DialogContent className="max-w-sm" data-testid="video-rating-dialog">
          <DialogHeader><DialogTitle className="font-heading text-base">How did "{rateVideo?.title}" feel?</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-2 py-2">
            {[1, 2, 3, 4, 5].map((r) => (
              <button key={r} type="button" onClick={() => setRating(r)} data-testid={`rating-star-${r}`}
                className="p-1 transition-transform hover:scale-110">
                <Star className={`h-8 w-8 ${rating >= r ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground h-5">{rating ? RATING_LABELS[rating] : 'Tap a star — this tunes next week\'s plan'}</p>
          <DialogFooter>
            <Button onClick={submitRating} disabled={!rating || saving} className="w-full" data-testid="rating-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & mark complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
